import Phaser from 'phaser';
import { audio } from '../game/AudioManager.js';
import { dialogueManager } from '../game/DialogueManager.js';
import { questManager } from '../game/QuestManager.js';
import { gameState } from '../game/GameState.js';
import { DIALOGUES } from '../data/dialogue.js';
import { enemyBarManager, playerBarManager } from '../game/UIManager.js';
import { TouchControls } from '../game/TouchControls.js';

export class Level3Scene extends Phaser.Scene {
  constructor() {
    super('Level3Scene');
    this.stage = 1; // 1: Outer Gates (Spear Vanguard), 2: Courtyard (Dual Shadow Knight), 3: High Spire (Council Champion)
    this.player = null;
    this.queen = null;
    this.boss = null;
    this.platforms = null;
    this.bossHp = 0;
    this.bossMaxHp = 0;
    this.playerHp = 5;
    this.playerMaxHp = 5;
    this.isAttacking = false;
    this.isInvulnerable = false;
    this.bossTimer = null;
    this.bossAttacking = false;
    this.sparkEmitter = null;
    this.isCutscene = false;
    this.cageGlow = null;
    this.queenLabel = null;
    this.advisor = null;
    this.advisorShadow = null;
    this.advisorLabel = null;
    this.advisorBubble = null;
    this.advisorEmote = null;
    this.advisorLastReaction = 0;
    this.bossPhase = 1;
    this.bossAura = null;
    this.bossInTelegraph = false;
    this.currentBossName = '';
    this.isDashing = false;
    this.lastDashTime = 0;
    this.comboCount = 0;
    this.lastHitTime = 0;
    this.comboText = null;
    this.groundHazardBox = null;
    this.touchMove = 0;
    this.touchMoveX = 0;
    this.touchMoveY = 0;
    this.controlsContainer = null;
    this.touchControls = null;
    this.bossHitCooldown = false;
  }

  init(data) {
    this.stage = data?.stage || 1;
  }

  create() {
    gameState.currentChapter = 3;
    questManager.show(true);
    questManager.setChapter("CHAPTER 3", "2026");
    audio.playHodBattleMusic();

    this.playerHp = 5;
    this.playerMaxHp = 5;
    playerBarManager.setLovePercent(100);

    this.cursors = this.input.keyboard.createCursorKeys();
    this.wasd = this.input.keyboard.addKeys({
      up: Phaser.Input.Keyboard.KeyCodes.W,
      down: Phaser.Input.Keyboard.KeyCodes.S,
      left: Phaser.Input.Keyboard.KeyCodes.A,
      right: Phaser.Input.Keyboard.KeyCodes.D,
      attack: Phaser.Input.Keyboard.KeyCodes.SPACE,
      fist: Phaser.Input.Keyboard.KeyCodes.F,
      jumpStrike: Phaser.Input.Keyboard.KeyCodes.J,
      thrust: Phaser.Input.Keyboard.KeyCodes.K,
      dash: Phaser.Input.Keyboard.KeyCodes.SHIFT,
      slashAlt: Phaser.Input.Keyboard.KeyCodes.Z,
      parryKey: Phaser.Input.Keyboard.KeyCodes.C
    });

    this.touchMove = 0;

    // Pointer click anywhere on canvas executes attack or parry towards click direction
    this.input.on('pointerdown', (pointer) => {
      if (this.isCutscene || !this.player || !this.player.active) return;
      const dlg = document.getElementById('dialogue');
      if (dlg && !dlg.classList.contains('hidden')) return;

      // Ignore touches on UI or on-screen touch control buttons
      const target = pointer.event && pointer.event.target;
      if (target && (target.closest('#touch-controls-root') || target.closest('#hud') || target.closest('button'))) return;

      // Ignore clicks on bottom action bar / control zone
      if (pointer.y > this.scale.height - 100) return;

      const clickWorldX = pointer.worldX !== undefined ? pointer.worldX : pointer.x;
      // Auto face toward click position
      if (clickWorldX < this.player.x - 15) {
        this.player.setFlipX(true);
      } else if (clickWorldX > this.player.x + 15) {
        this.player.setFlipX(false);
      }

      if (this.bossInTelegraph) {
        this.executeParryCounter();
      } else {
        this.executePlayerAttack('slash');
      }
    });

    if (this.stage === 1) {
      this.startStage1();
    } else if (this.stage === 2) {
      this.startStage2();
    } else {
      this.startStage3();
    }
  }

  createArenaPlatforms(theme = 'stone') {
    const { width, height } = this.scale;
    this.platforms = this.physics.add.staticGroup();

    // 1. Solid Battleground Floor (platformer floor matching visible top)
    const floorH = 48;
    const floorY = height - floorH / 2;
    const floorTexture = theme === 'carpet' ? 'tile_royal_ground' : 'tile_stone_ground';
    const floor = this.add.tileSprite(width / 2, floorY, width, floorH, floorTexture)
      .setDepth(4);
    this.platforms.add(floor);
    this.physics.add.existing(floor, true);
    floor.body.setSize(width, floorH).setOffset(0, 0);

    // Floor edge illumination / aesthetic rim
    const rimColor = theme === 'carpet' ? 0xffd166 : 0x10b981;
    this.add.rectangle(width / 2, height - floorH, width, 2, rimColor)
      .setDepth(5)
      .setAlpha(0.65);

    // 2. Elevated Platform Ledges for Dynamic 2D Platformer Combat
    const ledgeTexture = theme === 'carpet' ? 'platform_royal_220' : 'platform_ruin_220';
    const ledgeY = height - 150;

    // LEFT UPPER PLATFORM (x: 190, y: ledgeY)
    const leftLedge = this.add.image(190, ledgeY, ledgeTexture).setDepth(4);
    this.platforms.add(leftLedge);
    this.physics.add.existing(leftLedge, true);
    // Collision box aligned flush with the walkable top rim (no floating)
    leftLedge.body.setSize(220, 14).setOffset(0, 3);

    // UI DESIGN: Left Upper Platform Glow Rim (Breathing Neon UI Line)
    const glowColor = theme === 'carpet' ? 0xffd166 : 0x38bdf8;
    const leftGlow = this.add.rectangle(190, ledgeY - 14, 212, 3, glowColor)
      .setDepth(5)
      .setAlpha(0.7);
    this.tweens.add({
      targets: leftGlow,
      alpha: 0.25,
      duration: 1300,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });

    // UI DESIGN: Tactical Rune Badge on Left Platform
    const leftMarker = this.add.text(190, ledgeY - 26, '▲ HIGH GROUND', {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '10px',
      fontWeight: 'bold',
      color: theme === 'carpet' ? '#ffd166' : '#67e8f9',
      backgroundColor: 'rgba(15, 23, 42, 0.75)',
      padding: { x: 6, y: 1 }
    }).setOrigin(0.5).setDepth(6);
    this.tweens.add({
      targets: leftMarker,
      y: ledgeY - 29,
      duration: 1600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });

    // RIGHT UPPER PLATFORM (x: width - 190, y: ledgeY)
    const rightLedge = this.add.image(width - 190, ledgeY, ledgeTexture).setDepth(4);
    this.platforms.add(rightLedge);
    this.physics.add.existing(rightLedge, true);
    rightLedge.body.setSize(220, 14).setOffset(0, 3);

    // UI DESIGN: Right Upper Platform Glow Rim
    const rightGlow = this.add.rectangle(width - 190, ledgeY - 14, 212, 3, glowColor)
      .setDepth(5)
      .setAlpha(0.7);
    this.tweens.add({
      targets: rightGlow,
      alpha: 0.25,
      duration: 1300,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });

    // UI DESIGN: Royal Command Perch Banner hanging underneath right platform
    const rightBanner = this.add.text(width - 190, ledgeY + 26, '⚔️ COMMAND BATTLEMENT', {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '9px',
      fontWeight: 'bold',
      color: '#ffd166',
      backgroundColor: 'rgba(27, 13, 42, 0.85)',
      padding: { x: 6, y: 2 }
    }).setOrigin(0.5).setDepth(5);
    this.add.rectangle(width - 190, ledgeY + 26, rightBanner.width + 4, rightBanner.height + 2)
      .setStrokeStyle(1, 0xff70a6, 0.6)
      .setDepth(5);

    // Ambient floating mana motes / spores rising from the upper tiles
    this.add.particles(190, ledgeY - 10, 'petal_particle', {
      x: { min: -90, max: 90 },
      y: 0,
      lifespan: 2200,
      speedY: { min: -18, max: -40 },
      speedX: { min: -8, max: 8 },
      scale: { start: 0.5, end: 0.1 },
      alpha: { start: 0.6, end: 0 },
      quantity: 1,
      frequency: 600,
      tint: theme === 'carpet' ? 0xffd166 : 0x38bdf8
    }).setDepth(5);

    this.add.particles(width - 190, ledgeY - 10, 'petal_particle', {
      x: { min: -90, max: 90 },
      y: 0,
      lifespan: 2200,
      speedY: { min: -18, max: -40 },
      speedX: { min: -8, max: 8 },
      scale: { start: 0.5, end: 0.1 },
      alpha: { start: 0.6, end: 0 },
      quantity: 1,
      frequency: 600,
      tint: theme === 'carpet' ? 0xffd166 : 0x38bdf8
    }).setDepth(5);

    if (this.stage === 3) {
      // Center high platform under the throne cage (Grand Royal Dais)
      const centerHigh = this.add.image(width / 2, height - 250, 'platform_royal_center_240')
        .setDepth(4);
      this.platforms.add(centerHigh);
      this.physics.add.existing(centerHigh, true);
      centerHigh.body.setSize(240, 14).setOffset(0, 3);

      // Golden Dais Glow
      const centerGlow = this.add.rectangle(width / 2, height - 250 - 12, 230, 3, 0xffd166)
        .setDepth(5)
        .setAlpha(0.8);
      this.tweens.add({
        targets: centerGlow,
        alpha: 0.35,
        duration: 1100,
        yoyo: true,
        repeat: -1
      });
    }
  }

  // ==========================================
  // LEVEL 3 - STAGE 1: THE OUTER FORTRESS GATES
  // ==========================================
  startStage1() {
    this.stage = 1;
    this.isCutscene = false;
    if (this.bossTimer) this.bossTimer.remove();
    this.children.removeAll();
    const { width, height } = this.scale;

    this.add.image(0, 0, 'bg_moonlit_forest')
      .setOrigin(0, 0)
      .setDisplaySize(width, height)
      .setDepth(0);

    const atmosphere = this.add.graphics();
    atmosphere.fillGradientStyle(0x0f172a, 0x1e1b4b, 0x3b0764, 0x000000, 0.35);
    atmosphere.fillRect(0, 0, width, height);
    atmosphere.setDepth(1);

    this.createArenaPlatforms('stone');

    // Player Knight Talha (scale 0.40 matches other sprites)
    this.player = this.physics.add.sprite(160, height - 120, 'knight_battle_sheet')
      .setScale(0.40)
      .setDepth(10)
      .play('knight_battle_stance');
    this.player.setCollideWorldBounds(true);
    this.player.body.setGravityY(1100);
    this.player.body.setSize(56, 150).setOffset(100, 91);
    this.physics.add.collider(this.player, this.platforms);

    // Enemy: Corrupted Royal Spearman
    this.boss = this.physics.add.sprite(width - 180, height - 120, 'spear_knight_sheet')
      .setScale(0.42)
      .setDepth(10)
      .setFlipX(true)
      .play('spear_knight_idle');
    this.boss.setCollideWorldBounds(true);
    this.boss.body.setGravityY(1100);
    this.boss.body.setSize(64, 150).setOffset(96, 91);
    this.physics.add.collider(this.boss, this.platforms);

    this.bossMaxHp = 12;
    this.bossHp = 12;
    this.bossPhase = 1;
    this.bossAttacking = false;
    this.currentBossName = "Corrupted Royal Spearman";
    this.createHpBar("BATTLE 1/3: Corrupted Royal Spearman");
    this.createCombatControlsUI();

    // Royal Advisor commanding the outer fortress gates from the upper battlement
    this.createAdvisor(width - 120, height - 164, true);

    setTimeout(() => {
      if (this.advisor && this.advisor.active) {
        this.showAdvisorEmote('❗', '#f43f5e');
        this.showAdvisorBubble("Execute the trespasser!", 2200);
      }
      dialogueManager.start(DIALOGUES.level3_combat_intro, () => {
        questManager.setQuest("Defeat Royal Spearman", "[A/D] Move | [W] Jump | [SHIFT] Dash | [SPACE] Slash | [F] Parry | Dash + Slash = Dash Piercer!");
        this.setupPlatformerEnemyAI('spear_knight_walk', 'spear_knight_attack', 'spear_knight_idle', 150, 520);
      });
    }, 400);
  }

  // ==========================================
  // LEVEL 3 - STAGE 2: THE INNER COURTYARD
  // ==========================================
  startStage2() {
    this.stage = 2;
    this.isCutscene = false;
    if (this.bossTimer) this.bossTimer.remove();
    this.cameras.main.fadeIn(500);
    this.children.removeAll();
    const { width, height } = this.scale;

    this.add.image(0, 0, 'bg_throne_room')
      .setOrigin(0, 0)
      .setDisplaySize(width, height)
      .setDepth(0);

    const atmosphere = this.add.graphics();
    atmosphere.fillGradientStyle(0x4c0519, 0x1e1b4b, 0x3b0764, 0x000000, 0.3);
    atmosphere.fillRect(0, 0, width, height);
    atmosphere.setDepth(1);

    this.createArenaPlatforms('carpet');

    // Royal Advisor commanding from the courtyard upper parapet
    this.createAdvisor(width - 120, height - 164, true);
    this.time.delayedCall(450, () => {
      if (this.advisor && this.advisor.active) {
        this.showAdvisorEmote('⚡', '#ffd166');
        this.showAdvisorBubble("Shadow Assassin, tear him to shreds!", 2200);
      }
    });

    // Player Knight Talha
    this.player = this.physics.add.sprite(160, height - 120, 'knight_battle_sheet')
      .setScale(0.40)
      .setDepth(10)
      .play('knight_battle_stance');
    this.player.setCollideWorldBounds(true);
    this.player.body.setGravityY(1100);
    this.player.body.setSize(56, 150).setOffset(100, 91);
    this.physics.add.collider(this.player, this.platforms);

    // Enemy: Shadow Dual Blade Assassin
    this.boss = this.physics.add.sprite(width - 180, height - 120, 'dual_blade_knight_sheet')
      .setScale(0.42)
      .setDepth(10)
      .setFlipX(true)
      .play('dual_blade_idle');
    this.boss.setCollideWorldBounds(true);
    this.boss.body.setGravityY(1100);
    this.boss.body.setSize(64, 150).setOffset(96, 91);
    this.physics.add.collider(this.boss, this.platforms);

    this.bossMaxHp = 16;
    this.bossHp = 16;
    this.bossPhase = 1;
    this.bossAttacking = false;
    this.currentBossName = "Shadow Dual-Blade Assassin";
    this.createHpBar("BATTLE 2/3: Shadow Dual-Blade Assassin");
    this.createCombatControlsUI();

    questManager.setQuest("Defeat Shadow Assassin", "[SHIFT] Dash through strikes | Parry with [SPACE]/[F] | Mid-Air Slash = Helm Splitter!");
    this.setupPlatformerEnemyAI('dual_blade_walk', 'dual_blade_attack', 'dual_blade_idle', 190, 480);
  }

  // ==========================================
  // LEVEL 3 - STAGE 3: THE HIGH THRONE ARENA
  // ==========================================
  startStage3() {
    this.stage = 3;
    this.isCutscene = false;
    if (this.bossTimer) this.bossTimer.remove();
    this.cameras.main.fadeIn(600);
    this.children.removeAll();
    const { width, height } = this.scale;

    this.add.image(0, 0, 'bg_throne_room')
      .setOrigin(0, 0)
      .setDisplaySize(width, height)
      .setDepth(0);

    const atmosphere = this.add.graphics();
    atmosphere.fillGradientStyle(0x781932, 0xd4af37, 0x1e1b4b, 0x000000, 0.2);
    atmosphere.fillRect(0, 0, width, height);
    atmosphere.setDepth(1);

    this.createArenaPlatforms('carpet');

    // Royal Advisor cowering on the far parapet behind the High Champion
    this.createAdvisor(width - 110, height - 164, true);
    this.time.delayedCall(450, () => {
      if (this.advisor && this.advisor.active) {
        this.showAdvisorEmote('👑', '#ffd166');
        this.showAdvisorBubble("Champion, protect the Crimson Throne!", 2200);
      }
    });

    // Queen Labonno in the High Spire Throne (visual sprite, no physics body to get stuck)
    this.queen = this.add.sprite(width / 2, height - 310, 'queen_emily_sheet')
      .setScale(0.40)
      .setDepth(5)
      .play('queen_idle');

    const cage = this.add.graphics().setDepth(6);
    cage.lineStyle(3, 0xf43f5e, 0.9);
    cage.strokeCircle(this.queen.x, this.queen.y + 10, 55);
    this.cageGlow = cage;

    this.tweens.add({
      targets: cage,
      alpha: 0.35,
      duration: 1000,
      yoyo: true,
      repeat: -1
    });

    this.queenLabel = this.add.text(width / 2, height - 380, "🔒 Queen Labonno", {
      fontSize: '14px',
      color: '#ffd166',
      backgroundColor: 'rgba(0,0,0,0.75)',
      padding: { x: 8, y: 3 }
    }).setOrigin(0.5).setDepth(6);

    // Player Knight Talha
    this.player = this.physics.add.sprite(180, height - 120, 'knight_battle_sheet')
      .setScale(0.40)
      .setDepth(10)
      .play('knight_battle_stance');
    this.player.setCollideWorldBounds(true);
    this.player.body.setGravityY(1100);
    this.player.body.setSize(56, 150).setOffset(100, 91);
    this.physics.add.collider(this.player, this.platforms);

    // Boss: High Council Champion
    this.boss = this.physics.add.sprite(width - 200, height - 120, 'champion_boss_sheet')
      .setScale(0.45)
      .setDepth(10)
      .setFlipX(true)
      .play('boss_idle');
    this.boss.setCollideWorldBounds(true);
    this.boss.body.setGravityY(1100);
    this.boss.body.setSize(68, 150).setOffset(94, 91);
    this.physics.add.collider(this.boss, this.platforms);

    this.bossMaxHp = 20;
    this.bossHp = 20;
    this.bossPhase = 1;
    this.bossAttacking = false;
    this.currentBossName = "High Council Champion";
    this.createHpBar("FINAL BATTLE: High Council Champion");
    this.createCombatControlsUI();

    questManager.setQuest("DEFEAT THE CHAMPION & SAVE QUEEN LABONNO!", "Jump to high platforms during Cataclysm Shatter! [SHIFT] Dash | [SPACE] Parry!");
    this.setupPlatformerEnemyAI('boss_walk', 'boss_slash', 'boss_idle', 160, 600);
  }

  createHpBar(title) {
    enemyBarManager.show(title, this.bossHp, this.bossMaxHp);
  }

  // ==========================================
  // REAL 2D ACTION-PLATFORMER ENEMY AI
  // ==========================================
  setupPlatformerEnemyAI(walkAnim, attackAnim, idleAnim, moveSpeed, interval) {
    if (this.bossTimer) this.bossTimer.remove();

    this.bossTimer = this.time.addEvent({
      delay: interval,
      loop: true,
      callback: () => {
        if (!this.boss || !this.boss.active || !this.player || !this.player.active || this.bossAttacking) return;

        const effectiveSpeed = this.bossPhase === 2 ? moveSpeed * 1.35 : moveSpeed;
        const dist = Phaser.Math.Distance.Between(this.player.x, this.player.y, this.boss.x, this.boss.y);
        const dx = this.player.x - this.boss.x;
        const dy = this.player.y - this.boss.y;

        this.boss.setFlipX(dx < 0);

        // If player is on a higher ledge, enemy jumps up towards them
        if (dy < -60 && this.boss.body.blocked.down && Math.random() < 0.65) {
          this.boss.setVelocityY(-560);
          this.boss.setVelocityX(dx < 0 ? -effectiveSpeed * 1.1 : effectiveSpeed * 1.1);
          return;
        }

        // TACTICAL SPACING: If player crowds into extreme melee range, spearman leaps back to spear tip range!
        if (this.stage === 1 && dist < 85 && Math.random() < 0.32 && this.boss.body.blocked.down) {
          this.boss.setVelocityX(dx < 0 ? effectiveSpeed * 1.5 : -effectiveSpeed * 1.5);
          this.boss.setVelocityY(-260);
          this.boss.play('spear_knight_walk');
          return;
        }

        // Close Combat Range: Attack sequence with telegraph & parry window
        if (dist < 165) {
          this.bossAttacking = true;
          this.bossInTelegraph = true;
          this.boss.setVelocityX(0);

          // Visual windup pose: frame 8 is the battle windup / ready stance
          this.boss.setFrame(8);

          // 1. Telegraph attack: visual warning alert and crimson flash
          const alertBubble = this.add.text(this.boss.x, this.boss.y - 75, '⚠️ PARRY [F/C] / DASH [SHIFT]', {
            fontFamily: 'Trebuchet MS, Arial, sans-serif',
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#facc15',
            backgroundColor: 'rgba(15, 6, 23, 0.90)',
            padding: { x: 7, y: 3 }
          }).setOrigin(0.5).setDepth(25);

          this.boss.setTint(0xff5555);

          // 380ms parry opportunity window (crisp and readable)
          this.time.delayedCall(380, () => {
            if (alertBubble) alertBubble.destroy();
            // If parried or killed during telegraph window
            if (!this.boss || !this.boss.active || !this.bossInTelegraph) {
              this.bossInTelegraph = false;
              return;
            }
            this.bossInTelegraph = false;
            this.boss.clearTint();

            // ==========================================
            // THRILLING MULTI-PHASE ATTACK EXECUTION
            // ==========================================
            const isEnraged = this.bossPhase === 2;
            const roll = Math.random();

            if (this.stage === 1) {
              // ----------------------------------------
              // STAGE 1: ROYAL SPEARMAN
              // ----------------------------------------
              if (isEnraged && roll < 0.35) {
                // SPECIAL MOVE 1: Skyward Vault & Quake Slam!
                this.boss.setVelocityY(-620);
                this.boss.play('spear_knight_sweep');
                audio.playJump();

                this.time.delayedCall(360, () => {
                  if (!this.boss || !this.boss.active) return;
                  // Track player X position from air
                  this.boss.x = Phaser.Math.Linear(this.boss.x, this.player.x, 0.65);
                  this.boss.setVelocityY(720);

                  this.time.delayedCall(220, () => {
                    if (!this.boss || !this.boss.active) return;
                    this.cameras.main.shake(200, 0.012);
                    const floorY = this.scale.height - 48;
                    this.spawnShockwaveWave(this.boss.x, floorY, -1);
                    this.spawnShockwaveWave(this.boss.x, floorY, 1);
                    this.spawnThrustBeam(this.boss.x, this.boss.y, this.boss.flipX, true);
                    audio.playCastleRumble();
                  });
                });
              } else if (isEnraged && roll < 0.70) {
                // SPECIAL MOVE 2: Phantom Gale Thrust!
                const dashDir = this.boss.flipX ? -1 : 1;
                this.boss.setVelocityX(dashDir * 440);
                this.boss.play('spear_knight_attack');
                this.spawnThrustBeam(this.boss.x + dashDir * 55, this.boss.y - 10, this.boss.flipX, true);
                audio.playSwordSlash();
              } else if (isEnraged) {
                // SPECIAL MOVE 3: Whirlwind 3-Spear Flurry!
                this.boss.play('spear_knight_attack');
                const fDir = this.boss.flipX ? -1 : 1;
                this.spawnThrustBeam(this.boss.x + fDir * 50, this.boss.y - 10, this.boss.flipX, true);
                audio.playSwordSlash();

                this.time.delayedCall(160, () => {
                  if (!this.boss || !this.boss.active) return;
                  this.spawnThrustBeam(this.boss.x + fDir * 60, this.boss.y - 20, this.boss.flipX, true);
                  audio.playSwordSlash();
                });

                this.time.delayedCall(320, () => {
                  if (!this.boss || !this.boss.active) return;
                  this.boss.play('spear_knight_sweep');
                  this.spawnSlashArc(this.boss.x + fDir * 50, this.boss.y - 10, this.boss.flipX, true);
                  audio.playSwordSlash();
                });
              } else {
                // REGULAR SPEARMAN COMBAT: Crescent Sweep or Spear Thrust or Vault
                if (roll < 0.40) {
                  this.boss.play('spear_knight_sweep');
                  this.spawnSlashArc(this.boss.x + (this.boss.flipX ? -50 : 50), this.boss.y - 10, this.boss.flipX, true);
                  audio.playSwordSlash();
                } else if (roll < 0.75) {
                  this.boss.play('spear_knight_attack');
                  this.spawnThrustBeam(this.boss.x + (this.boss.flipX ? -55 : 55), this.boss.y - 10, this.boss.flipX, true);
                  audio.playSwordSlash();
                } else {
                  this.boss.setVelocityY(-480);
                  this.boss.setVelocityX(this.boss.flipX ? -160 : 160);
                  this.boss.play('spear_knight_sweep');
                  audio.playJump();
                  this.time.delayedCall(320, () => {
                    if (!this.boss || !this.boss.active) return;
                    this.spawnSlashArc(this.boss.x + (this.boss.flipX ? -45 : 45), this.boss.y - 10, this.boss.flipX, true);
                    audio.playSwordSlash();
                  });
                }
              }
            } else if (this.stage === 2) {
              // ----------------------------------------
              // STAGE 2: SHADOW DUAL-BLADE ASSASSIN
              // ----------------------------------------
              if (isEnraged && roll < 0.40) {
                // SPECIAL MOVE 1: Shadow Blink Strike!
                this.boss.setAlpha(0.2);
                this.spawnHitSparks(this.boss.x, this.boss.y);

                this.time.delayedCall(240, () => {
                  if (!this.boss || !this.boss.active) return;
                  const teleX = Math.random() < 0.65
                    ? this.player.x + (this.player.flipX ? 85 : -85)
                    : (this.boss.x < this.scale.width / 2 ? this.scale.width - 190 : 190);
                  const teleY = Math.random() < 0.65 ? this.player.y : this.scale.height - 150;
                  this.boss.setPosition(teleX, teleY);
                  this.boss.setAlpha(1);
                  this.boss.setFlipX(this.player.x < this.boss.x);
                  this.boss.play('dual_blade_attack');
                  this.spawnSlashArc(this.boss.x + (this.boss.flipX ? -45 : 45), this.boss.y - 10, this.boss.flipX, true);
                  audio.playSwordSlash();
                });
              } else if (isEnraged && roll < 0.75) {
                // SPECIAL MOVE 2: Triple Sonic Blade Barrage!
                this.boss.play('dual_blade_spin');
                const pDir = this.boss.flipX ? -1 : 1;
                this.spawnSonicBlade(this.boss.x + pDir * 40, this.boss.y - 15, pDir);
                this.time.delayedCall(140, () => {
                  if (this.boss && this.boss.active) {
                    this.spawnSonicBlade(this.boss.x + pDir * 40, this.boss.y, pDir);
                  }
                });
                this.time.delayedCall(280, () => {
                  if (this.boss && this.boss.active) {
                    this.spawnSonicBlade(this.boss.x + pDir * 40, this.boss.y + 12, pDir);
                  }
                });
              } else {
                // Regular dual blade cross-slash / spin
                const useAlt = roll < 0.5;
                this.boss.play(useAlt ? 'dual_blade_spin' : 'dual_blade_attack');
                this.spawnSlashArc(this.boss.x + (this.boss.flipX ? -45 : 45), this.boss.y - 10, this.boss.flipX, true);
                if (useAlt) {
                  this.boss.setVelocityY(-360);
                  this.boss.setVelocityX(this.boss.flipX ? -180 : 180);
                }
                audio.playSwordSlash();
              }
            } else {
              // ----------------------------------------
              // STAGE 3: HIGH COUNCIL CHAMPION
              // ----------------------------------------
              if (isEnraged && roll < 0.45) {
                this.triggerCataclysmShatter();
              } else if (isEnraged && roll < 0.80) {
                const cDir = this.boss.flipX ? -1 : 1;
                this.boss.setVelocityX(cDir * 380);
                this.boss.play('boss_slash');
                this.spawnSlashArc(this.boss.x + cDir * 55, this.boss.y - 12, this.boss.flipX, true);
                audio.playSwordSlash();
              } else {
                const useAlt = roll < 0.5;
                this.boss.play(useAlt ? 'boss_slam' : 'boss_slash');
                if (useAlt) {
                  const floorSurfaceY = this.scale.height - 48;
                  this.spawnShockwaveWave(this.boss.x, floorSurfaceY, this.boss.flipX ? -1 : 1);
                } else {
                  this.spawnSlashArc(this.boss.x + (this.boss.flipX ? -50 : 50), this.boss.y - 12, this.boss.flipX, true);
                }
                audio.playSwordSlash();
              }
            }

            // Check if player is caught in melee range
            const currentDist = Phaser.Math.Distance.Between(this.player.x, this.player.y, this.boss.x, this.boss.y);
            if (currentDist < 145 && !this.isDashing && !this.isInvulnerable) {
              this.damagePlayer(1);
            }

            // Safe recovery after attack animation finishes
            let attackFinished = false;
            const finishAttack = () => {
              if (attackFinished) return;
              attackFinished = true;
              if (this.boss && this.boss.active) {
                if (this.boss.anims.currentAnim?.key !== idleAnim) {
                  this.boss.play(idleAnim);
                }
                this.bossAttacking = false;
              }
            };

            this.boss.once('animationcomplete', finishAttack);
            // Safety fallback after 600ms (or 750ms for multi-part attacks)
            this.time.delayedCall(700, () => {
              if (this.bossAttacking) finishAttack();
            });
          });
        } else {
          // Advance towards player along platform smoothly
          if (this.boss.anims.currentAnim?.key !== walkAnim) {
            this.boss.play(walkAnim, true);
          }
          this.boss.setVelocityX(dx < 0 ? -effectiveSpeed : effectiveSpeed);
        }
      }
    });
  }

  damagePlayer(amount = 1) {
    if (this.isInvulnerable) return;

    this.playerHp -= amount;
    this.isInvulnerable = true;
    this.isAttacking = false; // Reset attack state on taking hit
    this.cameras.main.shake(160, 0.012);
    this.cameras.main.flash(200, 255, 60, 60, 0.3);
    audio.playPunch();
    this.reactAdvisor('cheer');

    // Knockback
    const knockDir = this.player.x < this.boss.x ? -160 : 160;
    this.player.setVelocityX(knockDir);
    this.player.setVelocityY(-200);

    // Update HUD Love Bond Bar
    const pct = Math.max(0, (this.playerHp / this.playerMaxHp) * 100);
    playerBarManager.setLovePercent(pct);

    // Invulnerability flashing
    this.tweens.add({
      targets: this.player,
      alpha: 0.3,
      duration: 100,
      yoyo: true,
      repeat: 4,
      onComplete: () => {
        if (this.player) {
          this.player.alpha = 1;
          this.isInvulnerable = false;
        }
      }
    });

    if (this.playerHp <= 0) {
      this.handlePlayerDefeat();
    }
  }

  handlePlayerDefeat() {
    this.isAttacking = false;
    this.isDashing = false;
    this.touchMove = 0;
    this.touchMoveX = 0;
    this.touchMoveY = 0;
    if (this.controlsContainer) {
      this.controlsContainer.destroy();
      this.controlsContainer = null;
    }
    this.player.setVelocity(0, 0);
    this.cameras.main.flash(400, 180, 20, 40, 0.6);
    questManager.setQuest("Our love will never yield! Stand back up and fight!");

    setTimeout(() => {
      this.playerHp = 5;
      playerBarManager.setLovePercent(100);
      if (this.stage === 1) this.startStage1();
      else if (this.stage === 2) this.startStage2();
      else this.startStage3();
    }, 1200);
  }

  spawnDamageNumber(x, y, text, color = '#ffd166') {
    const dmg = this.add.text(x, y - 20, text, {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '22px',
      fontWeight: 'bold',
      color: color,
      stroke: '#000000',
      strokeThickness: 4
    }).setOrigin(0.5).setDepth(30);

    this.tweens.add({
      targets: dmg,
      y: y - 70,
      alpha: 0,
      duration: 700,
      ease: 'Power1',
      onComplete: () => dmg.destroy()
    });
  }

  spawnHitSparks(x, y) {
    for (let i = 0; i < 6; i++) {
      const spark = this.add.ellipse(x + Phaser.Math.Between(-15, 15), y + Phaser.Math.Between(-15, 15), 6, 6, 0xffd166)
        .setDepth(25);
      this.tweens.add({
        targets: spark,
        x: spark.x + Phaser.Math.Between(-40, 40),
        y: spark.y + Phaser.Math.Between(-40, 40),
        alpha: 0,
        scale: 0.2,
        duration: 300,
        onComplete: () => spark.destroy()
      });
    }
  }

  spawnSlashArc(x, y, flipX, isVillain = false) {
    const texture = isVillain ? 'fx_crimson_slash' : 'fx_slash_arc';
    const arc = this.add.image(x, y, texture)
      .setScale(flipX ? -0.8 : 0.8, 0.8)
      .setDepth(18);
    arc.setAngle(flipX ? -15 : 15);

    this.tweens.add({
      targets: arc,
      scaleX: flipX ? -1.3 : 1.3,
      scaleY: 1.3,
      alpha: 0,
      duration: 220,
      ease: 'Cubic.easeOut',
      onComplete: () => arc.destroy()
    });
  }

  spawnThrustBeam(x, y, flipX, isVillain = false) {
    const beam = this.add.image(x, y, 'fx_thrust_beam')
      .setScale(flipX ? -0.8 : 0.8, 0.8)
      .setDepth(18);
    if (isVillain) beam.setTint(0xff3333);

    this.tweens.add({
      targets: beam,
      x: x + (flipX ? -90 : 90),
      scaleX: flipX ? -1.5 : 1.5,
      alpha: 0,
      duration: 200,
      ease: 'Power2',
      onComplete: () => beam.destroy()
    });
  }

  createAdvisor(x, y, flipX = true) {
    if (this.advisor) this.advisor.destroy();
    if (this.advisorShadow) this.advisorShadow.destroy();
    if (this.advisorLabel) this.advisorLabel.destroy();
    if (this.advisorBubble) this.advisorBubble.destroy();
    if (this.advisorEmote) this.advisorEmote.destroy();

    // Set origin to bottom center (0.5, 1) so feet are firmly planted right on top of the tile surface
    this.advisor = this.add.sprite(x, y, 'advisor_sprite')
      .setScale(0.38)
      .setOrigin(0.5, 1)
      .setFlipX(flipX)
      .setDepth(9);

    // Shadow right on top of the tile surface directly under his boots
    this.advisorShadow = this.add.ellipse(x, y, 36, 6, 0x000000, 0.45)
      .setDepth(8);

    // Label floating cleanly above his head
    this.advisorLabel = this.add.text(x, y - 104, '👑 Royal Advisor', {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '12px',
      color: '#ffd166',
      backgroundColor: 'rgba(27, 13, 42, 0.88)',
      padding: { x: 6, y: 2 }
    }).setOrigin(0.5).setDepth(15);

    // Living breathing animation keeping his feet grounded on the platform surface
    this.tweens.add({
      targets: this.advisor,
      scaleY: 0.366,
      duration: 1100,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  showAdvisorEmote(emoji, color = '#ffd166') {
    if (!this.advisor || !this.advisor.active) return;
    if (this.advisorEmote) this.advisorEmote.destroy();

    const em = this.add.text(this.advisor.x, this.advisor.y - 122, emoji, {
      fontSize: '22px'
    }).setOrigin(0.5).setDepth(25);
    this.advisorEmote = em;

    this.tweens.add({
      targets: em,
      y: em.y - 18,
      scale: 1.3,
      alpha: 0,
      duration: 1100,
      ease: 'Power1',
      onComplete: () => {
        if (em) em.destroy();
        if (this.advisorEmote === em) this.advisorEmote = null;
      }
    });
  }

  showAdvisorBubble(text, duration = 1400) {
    if (!this.advisor || !this.advisor.active) return;
    if (this.advisorBubble) this.advisorBubble.destroy();

    const bubble = this.add.container(this.advisor.x - 25, this.advisor.y - 126).setDepth(22);
    const label = this.add.text(0, 0, text, {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '12px',
      color: '#fff',
      backgroundColor: '#1b0d2a',
      padding: { x: 8, y: 4 }
    }).setOrigin(0.5);

    const border = this.add.rectangle(0, 0, label.width + 2, label.height + 2)
      .setStrokeStyle(1.5, 0xff70a6);

    bubble.add([border, label]);
    bubble.setScale(0.4);
    this.advisorBubble = bubble;

    this.tweens.add({
      targets: bubble,
      scale: 1,
      duration: 160,
      ease: 'Back.easeOut'
    });

    this.time.delayedCall(duration, () => {
      if (bubble && bubble.active) {
        this.tweens.add({
          targets: bubble,
          alpha: 0,
          scale: 0.8,
          duration: 200,
          onComplete: () => {
            if (bubble) bubble.destroy();
            if (this.advisorBubble === bubble) this.advisorBubble = null;
          }
        });
      }
    });
  }

  reactAdvisor(type) {
    if (!this.advisor || !this.advisor.active) return;
    const now = Date.now();
    if (now - this.advisorLastReaction < 1300) return;
    this.advisorLastReaction = now;

    if (type === 'shock') {
      const baseY = this.advisor.y;
      this.tweens.add({
        targets: this.advisor,
        y: baseY - 12,
        duration: 110,
        yoyo: true,
        ease: 'Quad.easeOut'
      });
      this.showAdvisorEmote('💦', '#60a5fa');
      const lines = [
        "Hold the line!",
        "Insolent knave!",
        "Do not let him pass!",
        "Defend the Spire!",
        "How is he this strong?!"
      ];
      this.showAdvisorBubble(Phaser.Utils.Array.GetRandom(lines), 1100);
    } else if (type === 'cheer') {
      this.tweens.add({
        targets: this.advisor,
        scaleY: 0.41,
        duration: 90,
        yoyo: true
      });
      this.showAdvisorEmote('👑', '#ffd166');
      const lines = [
        "Strike him down!",
        "For the Council!",
        "You shall never reach her!",
        "Execute the trespasser!"
      ];
      this.showAdvisorBubble(Phaser.Utils.Array.GetRandom(lines), 1100);
    }
  }

  spawnShockwaveWave(x, y, direction) {
    const ring = this.add.image(x, y, 'fx_shockwave_ring')
      .setScale(0.8, 0.8)
      .setDepth(14);
    audio.playCastleRumble();

    this.tweens.add({
      targets: ring,
      x: x + direction * 240,
      scaleX: 1.7,
      scaleY: 1.3,
      alpha: 0,
      duration: 480,
      ease: 'Power1',
      onComplete: () => ring.destroy()
    });

    // Check if player is grounded in shockwave path
    this.time.delayedCall(160, () => {
      if (!this.player || !this.player.body || this.isDashing || this.isInvulnerable) return;
      const onFloor = this.player.body.blocked.down || this.player.body.touching.down;
      const dist = Math.abs(this.player.x - x);
      if (onFloor && dist < 190) {
        this.damagePlayer(1);
      }
    });
  }

  spawnSonicBlade(x, y, direction) {
    audio.playSonicBlade();
    const blade = this.physics.add.sprite(x, y, 'fx_sonic_blade')
      .setScale(0.85)
      .setDepth(14);
    blade.body.setAllowGravity(false);
    blade.setVelocityX(direction * 390);
    blade.setFlipX(direction < 0);

    const checkHit = this.time.addEvent({
      delay: 35,
      repeat: 30,
      callback: () => {
        if (!blade || !blade.active || !this.player || !this.player.active) return;
        const d = Phaser.Math.Distance.Between(blade.x, blade.y, this.player.x, this.player.y);
        if (d < 48 && !this.isInvulnerable && !this.isDashing) {
          this.damagePlayer(1);
          blade.destroy();
          checkHit.remove();
        }
      }
    });

    this.time.delayedCall(1200, () => {
      if (blade && blade.active) blade.destroy();
    });
  }

  triggerCataclysmShatter() {
    if (!this.boss || !this.boss.active) return;
    this.boss.setVelocity(0, 0);
    this.boss.play('boss_slam');
    audio.playBossRoar();

    const { width, height } = this.scale;
    const floorY = height - 48;

    // 1. Red warning danger stripe along the entire floor
    const hazard = this.add.rectangle(width / 2, floorY + 24, width, 48, 0xef4444, 0.45)
      .setDepth(6);
    this.tweens.add({
      targets: hazard,
      alpha: 0.1,
      duration: 150,
      yoyo: true,
      repeat: 3
    });

    // 2. High-visibility danger warning text
    const warn = this.add.text(width / 2, height - 85, '⚠️ CATACLYSM HAZARD! JUMP TO HIGH PLATFORMS! ⚠️', {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '15px',
      fontWeight: 'bold',
      color: '#fef08a',
      backgroundColor: 'rgba(220, 38, 38, 0.88)',
      padding: { x: 12, y: 4 },
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5).setDepth(25);

    this.cameras.main.shake(550, 0.016);

    // 3. Shockwave detonation after 550ms
    this.time.delayedCall(550, () => {
      if (hazard) hazard.destroy();
      if (warn) warn.destroy();
      if (!this.boss || !this.boss.active) return;

      audio.playCastleRumble();
      this.spawnShockwaveWave(this.boss.x, floorY, -1);
      this.spawnShockwaveWave(this.boss.x, floorY, 1);
      this.spawnSlashArc(this.boss.x, floorY - 15, false, true);
      this.spawnSlashArc(this.boss.x, floorY - 15, true, true);

      // Check damage: ONLY grounded players on the bottom floor are damaged!
      if (this.player && this.player.body && !this.isDashing && !this.isInvulnerable) {
        const onFloor = (this.player.body.blocked.down || this.player.body.touching.down) && this.player.y > height - 80;
        if (onFloor) {
          this.damagePlayer(2); // Heavy Cataclysm damage
          this.spawnDamageNumber(this.player.x, this.player.y - 40, "CRUSHED! -2", "#ef4444");
        } else {
          // Dodged safely on upper platform or in air!
          this.spawnDamageNumber(this.player.x, this.player.y - 40, "SAFE ON HIGH GROUND! ✨", "#4ade80");
        }
      }
    });
  }

  triggerBossEnrage() {
    this.bossPhase = 2;
    audio.playBossRoar();
    this.cameras.main.shake(350, 0.015);
    this.cameras.main.flash(250, 255, 60, 60, 0.4);

    // Boss Enrage Aura
    if (this.bossAura) this.bossAura.destroy();
    this.bossAura = this.add.image(this.boss.x, this.boss.y, 'fx_boss_aura')
      .setScale(0.85)
      .setDepth(9)
      .setAlpha(0.85);

    this.tweens.add({
      targets: this.bossAura,
      scaleX: 1.15,
      scaleY: 1.15,
      alpha: 0.45,
      duration: 500,
      yoyo: true,
      repeat: -1
    });

    let enrageTitle = "⚠️ SPEARMAN ENRAGED! ⚠️";
    let advisorLine = "Unleash the forbidden blood spear!";
    if (this.stage === 2) {
      enrageTitle = "🌑 SHADOW FRENZY UNLEASHED! 🌑";
      advisorLine = "Tear him to shreds from the shadows!";
    } else if (this.stage === 3) {
      enrageTitle = "🔥 COUNCIL'S WRATH ACTIVATED! 🔥";
      advisorLine = "Crush him beneath your boots, Champion!";
      if (this.queen) {
        this.showAdvisorBubble("Talha, jump to the high platforms!", 2500);
      }
    }

    this.spawnBannerText(enrageTitle, '#ff2a75');
    this.reactAdvisor('shock');
    this.showAdvisorBubble(advisorLine, 2200);

    enemyBarManager.show(`⚡ ENRAGED: ${this.currentBossName}`, this.bossHp, this.bossMaxHp);
  }

  executeParryCounter() {
    this.bossInTelegraph = false;
    audio.playParry();

    // Ensure hero faces the boss during parry counter
    if (this.boss && this.boss.active && this.player && this.player.active) {
      this.player.setFlipX(this.boss.x < this.player.x);
    }

    const clashX = (this.player.x + this.boss.x) / 2;
    const clashY = (this.player.y + this.boss.y) / 2;

    // Parry Burst FX
    const parryBurst = this.add.image(clashX, clashY, 'fx_parry_ring')
      .setScale(0.5)
      .setDepth(30);
    this.tweens.add({
      targets: parryBurst,
      scale: 1.4,
      alpha: 0,
      duration: 350,
      ease: 'Cubic.easeOut',
      onComplete: () => parryBurst.destroy()
    });

    // Screen freeze and shake
    this.cameras.main.shake(200, 0.014);
    this.cameras.main.flash(100, 255, 235, 150, 0.5);

    // Stagger boss
    this.bossAttacking = true;
    this.boss.setVelocityX(this.player.x < this.boss.x ? 220 : -220);
    this.boss.setVelocityY(-160);
    this.boss.setTint(0xffd166);

    // Stagger duration
    this.time.delayedCall(1200, () => {
      if (this.boss && this.boss.active) {
        this.boss.clearTint();
        this.bossAttacking = false;
      }
    });

    // Deal 2 critical damage
    this.bossHp -= 2;
    this.spawnDamageNumber(this.boss.x, this.boss.y - 20, "✨ PARRY COUNTER! -2 ✨", "#ffd166");
    this.registerComboHit(true);

    enemyBarManager.updateHp(this.bossHp, this.bossMaxHp);
    this.reactAdvisor('shock');
    this.showAdvisorBubble("HE DEFLECTED IT?! HOW?!", 1400);

    if (this.bossHp <= 0) {
      this.handleBossDefeat();
    } else if (this.bossHp <= Math.ceil(this.bossMaxHp / 2) && this.bossPhase === 1) {
      this.triggerBossEnrage();
    }
  }

  registerComboHit(isParry = false) {
    const now = Date.now();
    if (now - this.lastHitTime < 1600) {
      this.comboCount++;
    } else {
      this.comboCount = 1;
    }
    this.lastHitTime = now;

    if (this.comboText) this.comboText.destroy();

    let comboLabel = `${this.comboCount}x COMBO!`;
    let comboColor = '#ffd166';
    if (isParry) {
      comboLabel = `✨ PARRY +${this.comboCount} ✨`;
      comboColor = '#fef08a';
    } else if (this.comboCount === 2) {
      comboLabel = `⚔️ 2x COMBO!`;
      comboColor = '#facc15';
    } else if (this.comboCount === 3) {
      comboLabel = `💥 3x CRIT STRIKE!`;
      comboColor = '#fb923c';
    } else if (this.comboCount >= 4) {
      comboLabel = `🔥 ${this.comboCount}x LOVE BLADE! 🔥`;
      comboColor = '#f43f5e';
      if (this.playerHp < this.playerMaxHp) {
        this.playerHp = Math.min(this.playerMaxHp, this.playerHp + 1);
        playerBarManager.setLovePercent((this.playerHp / this.playerMaxHp) * 100);
        this.spawnDamageNumber(this.player.x, this.player.y - 40, "+1 HEAL!", "#4ade80");
      }
    }

    this.comboText = this.add.text(this.player.x, this.player.y - 65, comboLabel, {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '16px',
      fontWeight: 'bold',
      color: comboColor,
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5).setDepth(28);

    this.tweens.add({
      targets: this.comboText,
      scaleX: 1.25,
      scaleY: 1.25,
      y: this.player.y - 85,
      alpha: 0,
      duration: 1000,
      ease: 'Power1',
      onComplete: () => {
        if (this.comboText) this.comboText.destroy();
      }
    });
  }

  spawnBannerText(text, color = '#ffd166') {
    const { width, height } = this.scale;
    const banner = this.add.text(width / 2, height * 0.32, text, {
      fontFamily: 'Trebuchet MS, Arial, sans-serif',
      fontSize: '22px',
      fontWeight: 'bold',
      color: color,
      backgroundColor: 'rgba(15, 6, 23, 0.88)',
      padding: { x: 18, y: 8 },
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5).setDepth(35).setScale(0.5);

    this.tweens.add({
      targets: banner,
      scale: 1.1,
      duration: 250,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.time.delayedCall(1200, () => {
          this.tweens.add({
            targets: banner,
            alpha: 0,
            y: banner.y - 30,
            duration: 350,
            onComplete: () => banner.destroy()
          });
        });
      }
    });
  }

  executeDash() {
    if (this.isDashing || Date.now() - this.lastDashTime < 650) return;

    // Check direction keys or analog stick for dash orientation
    const keyLeft = this.cursors?.left?.isDown || this.wasd?.left?.isDown;
    const keyRight = this.cursors?.right?.isDown || this.wasd?.right?.isDown;
    const touchX = this.touchMoveX || this.touchMove || 0;
    const wantsLeft = keyLeft || touchX < -0.15;
    const wantsRight = keyRight || touchX > 0.15;
    if (wantsLeft) {
      this.player.setFlipX(true);
    } else if (wantsRight) {
      this.player.setFlipX(false);
    }

    this.isDashing = true;
    this.lastDashTime = Date.now();
    this.isInvulnerable = true;
    audio.playDash();

    const dashDir = this.player.flipX ? -1 : 1;
    const dashSpeed = 480;
    this.player.setVelocityX(dashDir * dashSpeed);

    // Ghost afterimages
    for (let i = 0; i < 3; i++) {
      this.time.delayedCall(i * 50, () => {
        if (!this.player || !this.player.active) return;
        const ghost = this.add.sprite(this.player.x, this.player.y, this.player.texture.key, this.player.frame.name)
          .setScale(this.player.scaleX, this.player.scaleY)
          .setFlipX(this.player.flipX)
          .setDepth(8)
          .setTint(0x38bdf8)
          .setAlpha(0.65);
        this.tweens.add({
          targets: ghost,
          alpha: 0,
          scale: 0.35,
          duration: 220,
          onComplete: () => ghost.destroy()
        });
      });
    }

    this.time.delayedCall(220, () => {
      this.isDashing = false;
      this.isInvulnerable = false;
      if (this.player && this.player.body) {
        this.player.setVelocityX(0);
      }
    });
  }

  createCombatControlsUI() {
    if (this.controlsContainer) {
      this.controlsContainer.destroy();
      this.controlsContainer = null;
    }

    if (!this.touchControls) {
      this.touchControls = new TouchControls(this, 'combat', {
        onMove: (x, y) => {
          this.touchMoveX = x;
          this.touchMoveY = y;
          this.touchMove = x < -0.15 ? -1 : (x > 0.15 ? 1 : 0);
          // Allow upward joystick flick to jump
          if (y < -0.65 && this.player && this.player.body && (this.player.body.blocked.down || this.player.body.touching.down)) {
            this.player.setVelocityY(-560);
            audio.playJump();
          }
        },
        onJump: () => {
          if (this.player && this.player.body && (this.player.body.blocked.down || this.player.body.touching.down)) {
            this.player.setVelocityY(-560);
            audio.playJump();
          }
        },
        onSlash: () => {
          if (this.bossInTelegraph) this.executeParryCounter();
          else this.executePlayerAttack('slash');
        },
        onParry: () => {
          if (this.bossInTelegraph) this.executeParryCounter();
          else this.executePlayerAttack('fist');
        },
        onDash: () => {
          this.executeDash();
        },
        onCrit: () => {
          this.executePlayerAttack('jumpStrike');
        }
      });
      this.touchControls.mount();

      this.events.on('shutdown', () => {
        if (this.touchControls) {
          this.touchControls.destroy();
          this.touchControls = null;
        }
      });
    } else {
      this.touchControls.show(true);
    }
  }

  executePlayerAttack(type = 'slash') {
    if (this.isAttacking || !this.player || !this.player.active) return;

    // Check directional input or enemy position to ensure correct attack direction
    const keyLeft = this.cursors?.left?.isDown || this.wasd?.left?.isDown;
    const keyRight = this.cursors?.right?.isDown || this.wasd?.right?.isDown;
    const touchX = this.touchMoveX || this.touchMove || 0;
    const wantsLeft = keyLeft || touchX < -0.15;
    const wantsRight = keyRight || touchX > 0.15;

    if (wantsLeft) {
      this.player.setFlipX(true);
    } else if (wantsRight) {
      this.player.setFlipX(false);
    } else if (this.boss && this.boss.active) {
      // Auto-face towards the enemy when attacking if no movement input is held
      if (this.boss.x < this.player.x) {
        this.player.setFlipX(true);
      } else if (this.boss.x > this.player.x) {
        this.player.setFlipX(false);
      }
    }

    // CHECK IF PARRY WINDOW IS ACTIVE
    if (this.bossInTelegraph) {
      this.executeParryCounter();
      return;
    }

    const facingRight = !this.player.flipX;
    const hitDirection = facingRight ? 1 : -1;
    const onFloor = this.player.body && (this.player.body.blocked.down || this.player.body.touching.down);

    if (this.isDashing) {
      // SPECIAL MOVE: LUNGE DASH PIERCER
      this.isAttacking = true;
      audio.playSwordSlash();
      this.player.play('knight_battle_thrust');
      this.spawnThrustBeam(this.player.x + (hitDirection * 55), this.player.y - 8, this.player.flipX, false);
      this.checkHeroHit(2, 220);
      this.spawnDamageNumber(this.player.x, this.player.y - 45, '⚡ DASH PIERCER! ⚡', '#c084fc');
      this.time.delayedCall(240, () => {
        this.isAttacking = false;
        if (this.player && this.player.active) {
          this.player.play('knight_battle_stance', true);
        }
      });
      return;
    }

    if (!onFloor && type === 'slash') {
      // SPECIAL MOVE: AERIAL HELM SPLITTER
      this.isAttacking = true;
      audio.playSwordSlash();
      this.player.play('knight_battle_thrust');
      this.player.setVelocityY(280);
      this.spawnSlashArc(this.player.x + (hitDirection * 45), this.player.y + 10, this.player.flipX, false);
      this.checkHeroHit(2, 175);
      this.time.delayedCall(260, () => {
        this.isAttacking = false;
        if (this.player && this.player.active) {
          this.player.play('knight_battle_stance', true);
        }
      });
      return;
    }

    this.isAttacking = true;

    if (type === 'slash') {
      audio.playSwordSlash();
      this.player.play('knight_battle_slash');
      this.spawnSlashArc(this.player.x + (hitDirection * 45), this.player.y - 10, this.player.flipX, false);
      this.checkHeroHit(1, 165);

      this.time.delayedCall(260, () => {
        this.isAttacking = false;
        if (this.player && this.player.active) {
          this.player.play('knight_battle_stance', true);
        }
      });
    } else if (type === 'fist') {
      audio.playPunch();
      this.player.play('knight_battle_block');
      this.spawnHitSparks(this.player.x + (hitDirection * 40), this.player.y);
      this.checkHeroHit(1, 140);

      this.time.delayedCall(230, () => {
        this.isAttacking = false;
        if (this.player && this.player.active) {
          this.player.play('knight_battle_stance', true);
        }
      });
    } else if (type === 'thrust') {
      audio.playSwordSlash();
      this.player.play('knight_battle_thrust');
      this.spawnThrustBeam(this.player.x + (hitDirection * 50), this.player.y - 8, this.player.flipX, false);
      this.checkHeroHit(1, 210);

      this.time.delayedCall(280, () => {
        this.isAttacking = false;
        if (this.player && this.player.active) {
          this.player.play('knight_battle_stance', true);
        }
      });
    } else if (type === 'jumpStrike') {
      audio.playSwordSlash();
      this.player.play('knight_battle_slash');
      this.player.setVelocityY(-280);
      this.player.setVelocityX(hitDirection * 200);
      this.spawnSlashArc(this.player.x + (hitDirection * 45), this.player.y, this.player.flipX, false);
      this.checkHeroHit(2, 175);

      this.time.delayedCall(340, () => {
        this.isAttacking = false;
        if (this.player && this.player.active) {
          this.player.play('knight_battle_stance', true);
        }
      });
    }
  }

  update(time, delta) {
    if (!this.player || !this.player.body || this.isCutscene) return;

    // Check if dialogue is currently active
    const dlg = document.getElementById('dialogue');
    if (dlg && !dlg.classList.contains('hidden')) {
      this.player.setVelocityX(0);
      return;
    }

    // Keep Boss Enrage Aura attached to boss if active
    if (this.bossAura && this.boss && this.boss.active) {
      this.bossAura.setPosition(this.boss.x, this.boss.y);
    }

    const onFloor = this.player.body.blocked.down || this.player.body.touching.down;
    const runSpeed = 270;

    // 1. Dash Trigger (SHIFT key)
    if (Phaser.Input.Keyboard.JustDown(this.wasd.dash)) {
      this.executeDash();
      return;
    }

    if (this.isDashing) return;

    // 2. Attack Triggers
    if (Phaser.Input.Keyboard.JustDown(this.wasd.attack) || Phaser.Input.Keyboard.JustDown(this.wasd.slashAlt)) {
      if (this.bossInTelegraph) {
        this.executeParryCounter();
      } else {
        this.executePlayerAttack('slash');
      }
      return;
    }

    if (Phaser.Input.Keyboard.JustDown(this.wasd.fist) || Phaser.Input.Keyboard.JustDown(this.wasd.parryKey)) {
      if (this.bossInTelegraph) {
        this.executeParryCounter();
      } else {
        this.executePlayerAttack('fist');
      }
      return;
    }

    if (Phaser.Input.Keyboard.JustDown(this.wasd.thrust)) {
      this.executePlayerAttack('thrust');
      return;
    }

    if (Phaser.Input.Keyboard.JustDown(this.wasd.jumpStrike)) {
      this.executePlayerAttack('jumpStrike');
      return;
    }

    // If currently performing attack animation, let it play
    if (this.isAttacking) return;

    // 3. Movement
    let isMoving = false;
    const keyLeft = this.cursors.left.isDown || this.wasd.left.isDown;
    const keyRight = this.cursors.right.isDown || this.wasd.right.isDown;
    const touchX = this.touchMoveX || this.touchMove || 0;
    const wantsLeft = keyLeft || touchX < -0.15;
    const wantsRight = keyRight || touchX > 0.15;

    if (wantsLeft) {
      const speedScale = keyLeft ? 1 : Math.min(1, Math.max(0.45, Math.abs(touchX)));
      this.player.setVelocityX(-runSpeed * speedScale);
      this.player.setFlipX(true);
      isMoving = true;
    } else if (wantsRight) {
      const speedScale = keyRight ? 1 : Math.min(1, Math.max(0.45, Math.abs(touchX)));
      this.player.setVelocityX(runSpeed * speedScale);
      this.player.setFlipX(false);
      isMoving = true;
    } else {
      this.player.setVelocityX(0);
    }

    // 4. Jump
    const wantsJump = Phaser.Input.Keyboard.JustDown(this.wasd.up) ||
      Phaser.Input.Keyboard.JustDown(this.cursors.up);

    if (wantsJump && onFloor) {
      this.player.setVelocityY(-560);
      audio.playJump();
    }

    // 5. Animations
    if (!onFloor) {
      if (this.player.anims.currentAnim?.key !== 'knight_battle_thrust') {
        this.player.play('knight_battle_thrust', true);
      }
    } else {
      if (this.player.anims.currentAnim?.key !== 'knight_battle_stance') {
        this.player.play('knight_battle_stance', true);
      }
    }
  }

  checkHeroHit(damage, range = 165) {
    if (!this.boss || !this.boss.active) return;

    // Prevent spam-melting: 280ms hit invulnerability cooldown on boss
    if (this.bossHitCooldown) return;

    const dx = this.boss.x - this.player.x;
    const dy = Math.abs(this.boss.y - this.player.y);
    const facingBoss = (this.player.flipX && dx < 0) || (!this.player.flipX && dx > 0);

    // Hit if in front facing boss, or in tight melee range
    if ((facingBoss && Math.abs(dx) < range && dy < 130) || (Math.abs(dx) < 85 && dy < 110)) {
      // If hero hits during telegraph with a heavy move (Dash Piercer / Helm Splitter), interrupt boss!
      if (this.bossInTelegraph && damage >= 2) {
        this.executeParryCounter();
        return;
      }

      // BOSS TACTICAL GUARD & DEFLECT
      // In Stage 1 & 2, if boss is in neutral and hit from front with basic attack, 24% chance to deflect!
      const canDeflect = (this.stage === 1 || this.stage === 2) && !this.bossAttacking && !this.bossInTelegraph && damage === 1;
      if (canDeflect && Math.random() < 0.24) {
        audio.playParry();
        this.cameras.main.shake(80, 0.006);
        this.spawnHitSparks(this.boss.x, this.boss.y);
        this.spawnDamageNumber(this.boss.x, this.boss.y - 30, 'DEFLECTED! 🛡️', '#38bdf8');
        // Deflection bounce
        const bounceDir = dx > 0 ? 120 : -120;
        this.boss.setVelocityX(bounceDir);
        this.player.setVelocityX(-bounceDir);
        return;
      }

      this.bossHitCooldown = true;
      this.time.delayedCall(280, () => {
        this.bossHitCooldown = false;
      });

      this.registerComboHit(false);
      this.bossHp -= damage;
      this.cameras.main.shake(120, 0.009);
      this.spawnHitSparks(this.boss.x, this.boss.y);
      this.spawnDamageNumber(
        this.boss.x,
        this.boss.y,
        damage >= 2 ? `-${damage} CRIT!` : `-${damage}`,
        damage >= 2 ? '#ff70a6' : '#ffd166'
      );

      // Crunchy hitstop on critical hits
      if (damage >= 2) {
        this.triggerHitStop(40);
      }

      // Knockback on enemy
      const knockDir = dx > 0 ? 140 : -140;
      this.boss.setVelocityX(knockDir);
      this.boss.setVelocityY(-140);

      // Flash white
      this.boss.setTint(0xffffff);
      this.time.delayedCall(120, () => {
        if (this.boss && this.boss.active) this.boss.clearTint();
      });

      enemyBarManager.updateHp(this.bossHp, this.bossMaxHp);
      this.reactAdvisor('shock');

      if (this.bossHp <= 0) {
        this.handleBossDefeat();
      } else if (this.bossHp <= Math.ceil(this.bossMaxHp / 2) && this.bossPhase === 1) {
        this.triggerBossEnrage();
      }
    }
  }

  triggerHitStop(duration = 40) {
    if (this.hitStopActive) return;
    this.hitStopActive = true;
    this.physics.world.isPaused = true;
    this.time.delayedCall(duration, () => {
      this.physics.world.isPaused = false;
      this.hitStopActive = false;
    });
  }

  handleBossDefeat() {
    this.isCutscene = true;
    this.isAttacking = false;
    this.isDashing = false;
    this.touchMove = 0;
    if (this.controlsContainer) {
      this.controlsContainer.destroy();
      this.controlsContainer = null;
    }
    if (this.bossAura) {
      this.bossAura.destroy();
      this.bossAura = null;
    }
    if (this.player && this.player.body) {
      this.player.setVelocity(0, 0);
      this.player.play('knight_battle_stance', true);
    }
    enemyBarManager.hide();
    if (this.bossTimer) this.bossTimer.remove();
    if (this.boss) {
      this.boss.destroy();
      this.boss = null;
    }
    audio.playRomanticChime();

    // Freeze frame hit stop for visceral impact
    this.cameras.main.flash(300, 255, 215, 120, 0.4);

    if (this.stage === 1) {
      if (this.advisor && this.advisor.active) {
        this.showAdvisorEmote('😱', '#f43f5e');
        this.showAdvisorBubble("The Vanguard has fallen?! Sound the alarm!", 1800);
        this.advisor.setFlipX(false);
        this.tweens.add({
          targets: [this.advisor, this.advisorShadow, this.advisorLabel],
          x: this.scale.width + 120,
          duration: 750,
          ease: 'Power2',
          onComplete: () => {
            if (this.advisor) this.advisor.destroy();
            if (this.advisorShadow) this.advisorShadow.destroy();
            if (this.advisorLabel) this.advisorLabel.destroy();
          }
        });
      }
      dialogueManager.start(DIALOGUES.level3_stage1_clear, () => {
        this.cameras.main.fade(600, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.isCutscene = false;
          this.startStage2();
        });
      });
    } else if (this.stage === 2) {
      if (this.advisor && this.advisor.active) {
        this.showAdvisorEmote('😱', '#f43f5e');
        this.showAdvisorBubble("He breached the courtyard! Barricade the Spire!", 1800);
        this.advisor.setFlipX(false);
        this.tweens.add({
          targets: [this.advisor, this.advisorShadow, this.advisorLabel],
          x: this.scale.width + 120,
          duration: 750,
          ease: 'Power2',
          onComplete: () => {
            if (this.advisor) this.advisor.destroy();
            if (this.advisorShadow) this.advisorShadow.destroy();
            if (this.advisorLabel) this.advisorLabel.destroy();
          }
        });
      }
      dialogueManager.start(DIALOGUES.level3_stage2_clear, () => {
        this.cameras.main.fade(600, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.isCutscene = false;
          this.startStage3();
        });
      });
    } else if (this.stage === 3) {
      if (this.advisor && this.advisor.active) {
        this.showAdvisorEmote('💥', '#f43f5e');
        this.showAdvisorBubble("The High Champion is broken! Mercy! We surrender!", 2200);
        this.tweens.add({
          targets: this.advisor,
          scaleY: 0.28,
          duration: 300,
          ease: 'Bounce.easeOut'
        });
        this.time.delayedCall(800, () => {
          if (this.advisor && this.advisor.active) {
            this.advisor.setFlipX(false);
            this.tweens.add({
              targets: [this.advisor, this.advisorShadow, this.advisorLabel],
              x: this.scale.width + 120,
              duration: 700,
              ease: 'Power2'
            });
          }
        });
      }

      // Champion defeated! Shatter cage and free Queen Labonno immediately!
      if (this.cageGlow) {
        this.cageGlow.destroy();
        this.cageGlow = null;
      }
      if (this.queenLabel) {
        this.queenLabel.destroy();
        this.queenLabel = null;
      }

      if (this.touchControls) {
        this.touchControls.show(false);
      }

      questManager.setQuest("ESCAPE TOGETHER!", "Queen Labonno is free! Take her hand and escape!");
      audio.playCastleRumble();
      audio.playRomanticChime();
      this.cameras.main.shake(450, 0.015);
      this.cameras.main.flash(350, 255, 220, 150, 0.5);

      if (this.queen) {
        this.spawnHitSparks(this.queen.x, this.queen.y);
      }

      // Immediately trigger Queen Labonno's heroic drop-jump down to the floor
      this.time.delayedCall(200, () => {
        if (!this.queen || !this.player) return;

        // 1. Queen Labonno leaps from high throne cage
        this.queen.setDepth(15);
        this.queen.play('queen_leap');
        audio.playJump();

        // Exact floor Y where Talha stands
        const targetFloorY = this.player.y;
        const dropX = (this.queen.x + this.player.x) / 2;

        // 2. Parabolic arc drop-jump down directly onto the royal carpet floor
        this.tweens.add({
          targets: this.queen,
          x: dropX,
          y: targetFloorY,
          duration: 650,
          ease: 'Quad.easeIn',
          onComplete: () => {
            // 3. Land on her feet on the floor with impact!
            audio.playPunch();
            this.spawnHitSparks(this.queen.x, targetFloorY + 20);
            this.cameras.main.shake(140, 0.007);

            // 4. Face Talha and sprint across the carpet directly to him!
            this.queen.setFlipX(this.player.x < this.queen.x);
            this.queen.play('queen_run');

            const targetX = this.player.x + (this.queen.x < this.player.x ? -44 : 44);
            this.tweens.add({
              targets: this.queen,
              x: targetX,
              y: this.player.y,
              duration: 480,
              ease: 'Linear',
              onComplete: () => {
                // 5. Reach Talha face-to-face
                this.queen.play('queen_idle');
                this.player.play('knight_battle_stance');
                this.player.setFlipX(this.queen.x < this.player.x);
                audio.playRomanticChime();

                // Romantic heart burst between them
                const heart = this.add.text((this.player.x + this.queen.x) / 2, this.player.y - 70, '❤️', {
                  fontSize: '28px'
                }).setOrigin(0.5).setDepth(25);

                this.tweens.add({
                  targets: heart,
                  y: heart.y - 45,
                  alpha: 0,
                  scale: 1.5,
                  duration: 900,
                  onComplete: () => heart.destroy()
                });

                this.time.delayedCall(450, () => {
                  dialogueManager.start(DIALOGUES.level3_rescue_queen, () => {
                    this.cameras.main.shake(600, 0.02);
                    audio.playCastleRumble();
                    this.cameras.main.fade(800, 0, 0, 0);
                    this.cameras.main.once('camerafadeoutcomplete', () => {
                      this.scene.start('CastleEscapeScene');
                    });
                  });
                });
              }
            });
          }
        });
      });
    }
  }
}
